/**
 * One place that decides what a failed `supabase.functions.invoke` actually
 * tells us.
 *
 * Every call site used to hand-roll the same `FunctionsHttpError` unwrap, and
 * the copies had drifted: some recovered the HTTP status on a bodiless non-2xx
 * (DEV-504) and some lost it, and only one of them noticed that a transport
 * failure is not an answer at all. The distinction is not cosmetic — an action
 * that mutates (sends an email, spends money) needs to know the difference
 * between "the function said no" and "we never heard", because only the first
 * one makes a retry free.
 *
 * supabase-js raises exactly three shapes (see `FunctionsClient.invoke`):
 *   - `FunctionsHttpError` — a real non-2xx response. A definite answer.
 *   - `FunctionsFetchError` — `fetch` itself rejected: offline, DNS, CORS, a
 *     dropped connection. No answer; the request may still have been served.
 *   - `FunctionsRelayError` — the relay reported a failure. It carries a
 *     Response, but it says nothing about whether the function ran, so it is
 *     indeterminate too.
 * Anything else reaching the caller (e.g. a body read rejecting mid-stream) is
 * likewise an absence of an answer, so `unknown` is the safe default.
 */
import { FunctionsHttpError } from "@supabase/supabase-js";

export type FunctionsFailure =
  /** A real non-2xx: the function answered, and the answer was no. */
  | { kind: "http"; status: number; body: Record<string, unknown>; message: string }
  /** No usable answer came back. The action may or may not have happened. */
  | { kind: "unknown"; message: string };

/**
 * Read a failed invoke into a `FunctionsFailure`.
 *
 * Async because the error body can only be recovered by consuming
 * `error.context`, which is a `Response`. A body that is missing or not JSON
 * degrades to `{}` rather than throwing — a failure path must not fail.
 */
/**
 * Best available message for anything thrown.
 *
 * Not just `error instanceof Error`: the invoke result is typed loosely enough
 * that a bare `{ message }` reaches here (mocks and older client shapes both do
 * it), and `String(error)` would render that as "[object Object]" — losing the
 * one diagnosable fact the failure carried.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === "string") return message;
  }
  return String(error);
}

export async function classifyFunctionsError(error: unknown): Promise<FunctionsFailure> {
  const message = errorMessage(error);
  if (!(error instanceof FunctionsHttpError)) return { kind: "unknown", message };

  const body = (await error.context.json().catch(() => ({}))) as Record<string, unknown>;
  return { kind: "http", status: error.context.status, body, message };
}

/**
 * The most specific thing we can say about the failure, in this order: the
 * worker's or edge function's own `error` string, then the bare status, then
 * supabase-js's generic wording.
 *
 * The status fallback is load-bearing (DEV-504): a bodiless non-2xx otherwise
 * reaches Sentry as "Edge Function returned a non-2xx status code", naming
 * neither the status nor the reason.
 */
export function functionsFailureMessage(failure: FunctionsFailure): string {
  if (failure.kind === "unknown") return failure.message;
  const detail = failure.body.error;
  if (typeof detail === "string" && detail) return detail;
  return failure.status ? `HTTP ${failure.status}` : failure.message;
}

/**
 * Statuses our own edge proxy returns when it could not get a verdict from the
 * worker: `forwardToWorker` answers 504 when it aborted a worker that was still
 * running, and 502 when the connection died. In both cases the request may have
 * reached the worker and been carried out.
 */
const INDETERMINATE_STATUSES = new Set([502, 504]);

/**
 * True when the failure does NOT prove the action was skipped.
 *
 * Callers whose action is idempotent can ignore this. Callers that send email
 * or spend money must not report an indeterminate failure as a definite one —
 * that is the difference between "nothing happened" and "we don't know".
 */
export function isIndeterminate(failure: FunctionsFailure): boolean {
  return failure.kind === "unknown" || INDETERMINATE_STATUSES.has(failure.status);
}

/**
 * Classify and render in one step, for the many callers that only want a message
 * to throw. Callers that must branch on indeterminacy classify explicitly.
 */
export async function describeFunctionsError(error: unknown): Promise<string> {
  return functionsFailureMessage(await classifyFunctionsError(error));
}
